import { canvasPanelClassNames } from "../canvasTheme";
import { CAD_COLOR_SET } from "../cadColors";
import { LINE_TYPE_OPTIONS, type EntityTypeStyle } from "../settingsPanelTypes";
import { SelectField, TextInputField } from "./SettingsPanelFields";

type UpdateEntityTypeStyle = (field: keyof EntityTypeStyle, value: EntityTypeStyle[keyof EntityTypeStyle]) => void;

const vertexShapeOptions = [
  { value: "circle", label: "Circle" },
  { value: "square", label: "Square" },
  { value: "cross", label: "Cross" },
] as const;

const labelPositionOptions = [
  { value: "bottom", label: "Bottom of line" },
  { value: "middle", label: "Middle of line" },
  { value: "top", label: "Top of line" },
] as const;

type ColorFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
};

function ColorField({ label, value, onChange }: ColorFieldProps) {
  const options = CAD_COLOR_SET.some((color) => color.value.toLowerCase() === value.toLowerCase())
    ? CAD_COLOR_SET
    : [...CAD_COLOR_SET, { name: "1" as const, label: `Custom - ${value}`, value }];

  return (
    <label className={canvasPanelClassNames.fieldLabel}>
      {label}
      <span className="flex items-center gap-2">
        <span
          className="h-8 w-8 shrink-0 rounded-md border border-[rgba(74,58,31,0.18)]"
          style={{ backgroundColor: value }}
          aria-hidden="true"
        />
        <select
          className={canvasPanelClassNames.selectControl}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        >
          {options.map((color) => (
            <option key={`${color.name}-${color.value}`} value={color.value}>
              {color.label}
            </option>
          ))}
        </select>
      </span>
    </label>
  );
}

type EntityStyleFieldsProps = {
  style: EntityTypeStyle;
  updateStyle: UpdateEntityTypeStyle;
};

export function EntityStyleFields({ style, updateStyle }: EntityStyleFieldsProps) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <ColorField
        label="Stroke Color"
        value={style.strokeColor}
        onChange={(value) => updateStyle("strokeColor", value)}
      />
      <TextInputField
        label="Stroke Width"
        type="number"
        min={0.5}
        max={12}
        step={0.5}
        value={style.strokeWidth}
        onChange={(value) => updateStyle("strokeWidth", Math.max(0.5, Number(value) || 0.5))}
      />
      <SelectField
        label="Line Type"
        value={style.lineType}
        options={[...LINE_TYPE_OPTIONS]}
        onChange={(value) => updateStyle("lineType", value)}
      />
      <SelectField
        label="Vertex Shape"
        value={style.vertexShape}
        options={[...vertexShapeOptions]}
        onChange={(value) => updateStyle("vertexShape", value)}
      />
      <ColorField
        label="Vertex Color"
        value={style.vertexColor}
        onChange={(value) => updateStyle("vertexColor", value)}
      />
      <TextInputField
        label="Vertex Size"
        type="number"
        min={2}
        max={12}
        step={0.5}
        value={style.vertexSize}
        onChange={(value) => updateStyle("vertexSize", Math.max(2, Number(value) || 2))}
      />
      <ColorField label="Grip Color" value={style.gripColor} onChange={(value) => updateStyle("gripColor", value)} />
      <TextInputField
        label="Grip Size"
        type="number"
        min={2}
        max={12}
        step={0.5}
        value={style.gripSize}
        onChange={(value) => updateStyle("gripSize", Math.max(2, Number(value) || 2))}
      />
      <TextInputField
        label="Letter"
        value={style.labelText}
        onChange={(value) => updateStyle("labelText", value)}
      />
      <TextInputField
        label="Letter Size"
        type="number"
        min={1}
        max={200}
        step={1}
        value={style.labelHeight}
        onChange={(value) => updateStyle("labelHeight", Math.max(1, Number(value) || 1))}
      />
      <SelectField
        label="Letter Position"
        value={style.labelPosition}
        options={[...labelPositionOptions]}
        onChange={(value) => updateStyle("labelPosition", value)}
      />
    </div>
  );
}
